import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X, CheckCircle2, AlertTriangle } from 'lucide-react';

/**
 * HAND OFF TO PLAN (CONTEXT-DOCS.md §H3) — the ONE door from Context to Plan, so nothing is ambiguous:
 *   1. Context — the version Plan will work from (the active one) · Build first when there's unbuilt material ·
 *      it must be APPROVED (a lead approves it right here — one click, never an extra trip);
 *   2. Still open — open questions / disputed / unclear (they don't block — Plan carries them as Gaps);
 *   3. Documents — every READ document, all ticked; untick what Plan shouldn't get;
 *   4. Agent outputs picked with "Use in Plan";
 *   5. Files for Plan — added in Plan mode with + (they go to uploads/plan/).
 * Confirm = "Plan from vN" → a frozen pack (uploads/plan/context/) + the switch to Plan.
 */
export function handoffState(ctxBuild) {
  const b = ctxBuild || {};
  const versions = b.versions || [];
  const active = versions.find((v) => v.active) || null;
  const pend = b.pending || {};
  return {
    active,
    unbuilt: Number(pend.messages) > 0,
    reading: Number(pend.reading) || 0,
    needsApproval: !!active && !active.approved,
    readiness: b.readiness || {},
    plan: b.plan || {},
  };
}

export default function HandoffDialog({ open, ctxBuild, docs = [], canLead = false, onBuild, onConfirm, onCancel,
  busy = false, error = '' }) {
  const st = handoffState(ctxBuild);
  const [unticked, setUnticked] = useState({});
  useEffect(() => { if (open) setUnticked({}); }, [open]);
  const chosen = useMemo(() => docs.filter((d) => !unticked[d.sha]).map((d) => d.sha), [docs, unticked]);
  if (!open) return null;
  const v = st.active;
  const building = (ctxBuild || {}).status === 'building';
  const blocked = !v || st.unbuilt || building || (!canLead);
  const label = !v ? 'Build the context first' : st.needsApproval ? `Approve v${v.v} and plan from it` : `Plan from v${v.v}`;
  const rd = st.readiness;
  const open_ = [['unclear', 'unclear'], ['disputed', 'disputed']].filter(([k]) => Number(rd[k]) > 0);
  const picks = (st.plan.picks || []);
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true"
      aria-label="Hand off to Plan" data-testid="handoff-dialog">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-gray-200 px-5 py-3">
          <div className="flex-1">
            <div className="text-[0.9375rem] font-bold text-gray-900">Hand off to Plan</div>
            <div className="text-[0.75rem] text-gray-500">Plan works from an approved context — frozen, so the plan is reproducible.</div>
          </div>
          <button type="button" onClick={onCancel} aria-label="Stay in Context" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100">
            <X className="h-[1.125rem] w-[1.125rem]" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 text-[0.8125rem]">
          <section data-testid="handoff-context">
            <div className="mb-1 text-[0.75rem] font-semibold uppercase tracking-wide text-gray-500">1 · The context</div>
            {!v ? (
              <div className="text-gray-700">There&apos;s no context yet — build it from the discussion first.</div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{v.title}</span>
                {v.approved
                  ? <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[0.6875rem] font-medium text-emerald-700"><CheckCircle2 className="h-3 w-3" aria-hidden />Approved by {v.approvedBy}</span>
                  : <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[0.6875rem] font-medium text-amber-800">Not approved yet</span>}
              </div>
            )}
            {st.unbuilt ? (
              <div className="mt-1.5 flex items-center gap-2 text-amber-800" data-testid="handoff-unbuilt">
                <AlertTriangle className="h-4 w-4" aria-hidden /> There&apos;s new material not in the context yet.
                <button type="button" onClick={onBuild} disabled={building}
                  className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">
                  {building ? 'Building…' : 'Build first'}
                </button>
              </div>
            ) : null}
            {st.reading ? <div className="mt-1 text-[0.75rem] text-gray-500">{st.reading} document{st.reading === 1 ? ' is' : 's are'} still being read — they&apos;ll join a later version.</div> : null}
            {!canLead ? <div className="mt-1 text-[0.75rem] text-gray-500">Only the room&apos;s owner or a builder can hand off to Plan.</div> : null}
          </section>
          <section data-testid="handoff-open">
            <div className="mb-1 text-[0.75rem] font-semibold uppercase tracking-wide text-gray-500">2 · Still open</div>
            {open_.length
              ? <div className="text-gray-700">{open_.map(([k, l]) => `${rd[k]} ${l}`).join(' · ')} — Plan carries open points as Gaps; they don&apos;t block.</div>
              : <div className="text-gray-500">Nothing disputed or unclear. Open questions in the context still go to Gaps.</div>}
          </section>
          <section data-testid="handoff-docs">
            <div className="mb-1 text-[0.75rem] font-semibold uppercase tracking-wide text-gray-500">3 · Documents ({chosen.length} of {docs.length})</div>
            {docs.length ? (
              <ul className="space-y-0.5">
                {docs.map((d) => (
                  <li key={d.sha}>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input type="checkbox" checked={!unticked[d.sha]} data-testid={`handoff-doc-${d.sha}`}
                        onChange={() => setUnticked((u) => ({ ...u, [d.sha]: !u[d.sha] }))} />
                      <span className="truncate text-gray-800">{d.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : <div className="text-gray-500">No documents shared yet.</div>}
          </section>
          <section data-testid="handoff-outputs">
            <div className="mb-1 text-[0.75rem] font-semibold uppercase tracking-wide text-gray-500">4 · Agent outputs ({picks.length})</div>
            {picks.length
              ? <ul className="space-y-0.5">{picks.map((p) => <li key={p.id} className="truncate text-gray-800">{p.name} <span className="text-gray-500">— made by {p.agentId}</span></li>)}</ul>
              : <div className="text-gray-500">None picked. Use &ldquo;Use in Plan&rdquo; on an agent&apos;s work to include it.</div>}
          </section>
          <section>
            <div className="mb-1 text-[0.75rem] font-semibold uppercase tracking-wide text-gray-500">5 · Files for Plan</div>
            <div className="text-gray-500">Add more in Plan mode with + — they go to <code>uploads/plan/</code>.</div>
          </section>
          {error ? <div className="text-rose-600" data-testid="handoff-error">{error}</div> : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" onClick={onCancel}
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">Stay in Context</button>
          <button type="button" data-testid="handoff-confirm" disabled={blocked || busy}
            onClick={() => onConfirm({ version: v && v.v, docs: chosen, approve: st.needsApproval })}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}{label}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** PLAN'S BASIS (CONTEXT-DOCS §H3) — in Plan mode, what the plan is made from; drift when the context moved on. */
export function PlanBasis({ plan, onUpdate }) {
  const h = plan && plan.handoff;
  if (!h) {
    return <span data-testid="plan-basis" className="text-[11px] text-amber-700">No approved context handed to Plan yet</span>;
  }
  return (
    <span data-testid="plan-basis" className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px]">
      <span className="text-gray-500">Planning from <span className="font-medium text-gray-800">Context v{h.version}</span>
        {h.approvedBy ? ` (approved by ${h.approvedBy})` : ''}</span>
      {plan.drift ? (
        <span data-testid="plan-drift" className="inline-flex items-center overflow-hidden rounded-md ring-1 ring-amber-200">
          <span className="bg-amber-50 px-2 py-0.5 text-amber-800">The context changed (v{plan.activeVersion})</span>
          {typeof onUpdate === 'function' ? (
            <button type="button" onClick={onUpdate} data-testid="plan-drift-update"
              className="border-l border-amber-200 bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-indigo-700">Update the plan input</button>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
